/**
 * WidgetTile — A single widget card in the library grid.
 *
 * Shows a Lucide icon, widget name, and 1-line description.
 * Tap to add the widget to the page.
 */

import {
  Layout,
  Grid3X3,
  LayoutGrid,
  ShoppingBag,
  Star,
  CalendarDays,
  List,
  Megaphone,
  Type,
  Frame,
  Image,
} from 'lucide-react'
import type { WidgetDefinition } from '@zaviago/storefront-config'

/** Map section type → Lucide icon for the library grid */
const WIDGET_ICONS: Record<string, React.ElementType> = {
  'hero': Layout,
  'product-grid': Grid3X3,
  'category-grid': LayoutGrid,
  'product-carousel': ShoppingBag,
  'featured-products': Star,
  'products-week': CalendarDays,
  'static-categories': List,
  'announcement-bar': Megaphone,
  'text-block': Type,
  'container-frame': Frame,
  'image-slideshow': Image,
  'code:exogro-header': Layout,
  'code:exogro-footer': Layout,
  'code:exogro-product-card': ShoppingBag,
}

interface WidgetTileProps {
  widget: WidgetDefinition
  onAdd: (type: string) => void
}

export function WidgetTile({ widget, onAdd }: WidgetTileProps) {
  const Icon = WIDGET_ICONS[widget.type] ?? Layout

  return (
    <button
      className="flex flex-col items-center gap-2 rounded border border-gray-200 bg-white p-3 text-left transition-all hover:border-sky-300 hover:shadow-apple-sm active:scale-[0.98]"
      onClick={() => onAdd(widget.type)}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-gray-50 text-gray-400">
        <Icon className="h-6 w-6" />
      </div>
      <div className="w-full text-center">
        <p className="text-xs font-medium text-gray-700 truncate">
          {widget.schema.label}
        </p>
        <p className="mt-0.5 text-[10px] text-gray-400 truncate">
          {widget.nameTh}
        </p>
      </div>
    </button>
  )
}
